import { useState } from "react";
import { X, MessageCircle, Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { trackOnce } from "../lib/pixel";

const SUPPORT = {
  whatsapp_link: "https://wa.me/918107649663",
  whatsapp_display: "+91 8107649663",
  email: "support@chartzfx.com",
};

/**
 * Contact Support Modal — universal upgrade-request UI.
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - desiredPlan: optional plan code being requested (STARTER|PROFESSIONAL|BUSINESS|ENTERPRISE)
 *  - title: optional heading override
 *  - subtitle: optional
 */
export default function ContactSupportModal({ open, onClose, desiredPlan = null,
                                              title = "Want to upgrade your subscription?",
                                              subtitle = "Please contact our support team to upgrade or activate a plan." }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const whatsappMsg = encodeURIComponent(
    `Hi ChartzFx, I'd like to ${desiredPlan ? `upgrade to the ${desiredPlan} plan` : "upgrade my subscription"}.${message ? `\n\n${message}` : ""}`
  );
  const waHref = `${SUPPORT.whatsapp_link}?text=${whatsappMsg}`;
  const emailSubject = encodeURIComponent(desiredPlan ? `Subscription upgrade: ${desiredPlan}` : "Subscription upgrade request");
  const emailBody = encodeURIComponent(`Hi ChartzFx team,\n\nI'd like to upgrade my subscription${desiredPlan ? ` to the ${desiredPlan} plan` : ""}.\n\n${message}\n\nThanks.`);
  const emailHref = `mailto:${SUPPORT.email}?subject=${emailSubject}&body=${emailBody}`;

  const submitRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/upgrade-request", {
        desired_plan: desiredPlan,
        message,
      });
      if (!data?.duplicate && data?.request_id) trackOnce("Lead", data.request_id);
      setDone(true);
      toast.success("Upgrade request sent to admin");
    } catch {
      toast.error("Could not submit request. Please try WhatsApp or Email.");
    } finally { setBusy(false); }
  };

  const close = () => { setDone(false); setMessage(""); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="contact-support-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
      <div className="relative card-dark w-full max-w-md p-6 sm:p-8 border border-white/10">
        <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="contact-modal-close">
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-[#00E676] mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Request received</h3>
            <p className="text-sm text-gray-400">Our support team will contact you shortly. For faster response, message us on WhatsApp.</p>
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 mt-6 px-6 py-2 text-sm">
              <MessageCircle size={15} /> WhatsApp Support
            </a>
          </div>
        ) : (
          <>
            <h3 className="text-xl text-white mb-1">{title}</h3>
            <p className="text-sm text-gray-400 mb-5">{subtitle}</p>

            {desiredPlan && (
              <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded px-3 py-2 mb-4">
                <p className="text-xs text-gray-400">Requesting plan</p>
                <p className="text-sm text-[#00F0FF] font-medium">{desiredPlan}</p>
              </div>
            )}

            <label className="label-xs">Message to support (optional)</label>
            <textarea
              data-testid="contact-modal-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any details we should know…"
              className="input-dark resize-none mt-1 mb-5"
              maxLength={1000}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <a href={waHref} target="_blank" rel="noopener noreferrer"
                 data-testid="contact-modal-whatsapp"
                 className="btn-outline flex items-center justify-center gap-2 py-2.5 text-sm bg-[#25D366]/10 border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/20">
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a href={emailHref}
                 data-testid="contact-modal-email"
                 className="btn-outline flex items-center justify-center gap-2 py-2.5 text-sm">
                <Mail size={15} /> Email
              </a>
              <button
                data-testid="contact-modal-request"
                disabled={busy}
                onClick={submitRequest}
                className="btn-primary flex items-center justify-center gap-2 py-2.5 text-sm disabled:opacity-50">
                <Send size={15} /> {busy ? "Sending…" : "Request Upgrade"}
              </button>
            </div>

            <div className="mt-5 pt-4 border-t border-white/5 text-xs text-gray-500 space-y-1">
              <p>WhatsApp: <a href={waHref} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">{SUPPORT.whatsapp_display}</a></p>
              <p>Email: <a href={`mailto:${SUPPORT.email}`} className="text-[#00F0FF] hover:underline">{SUPPORT.email}</a></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { SUPPORT };
